// Vordefinierte Toleranzketten als Onboarding-Beschleuniger.
//
// Jedes Template ist eine fertige Berechnung, die der User mit einem
// Klick laden kann. Free-Feature: dient als "Probier-mich-aus"-Einstieg
// und soll keine Counter-Berechnung verbrauchen. Templates werden über
// die Tolerance-Store-Action loadProject geladen, das setzt counted=true
// ohne den Backend-Counter zu erhöhen.
//
// Das Format hier ist absichtlich nicht das .tcp-Envelope: wir bauen die
// reine "data"-Section, weil Templates statisch sind und kein
// created_at/Metadata brauchen. Der Tolerance-Store loadProject akzeptiert
// die {elements, m0, mode}-Form direkt und re-canonicalizes elements
// via makeElement, sodass jedes Template eine valide v2.2-Form bekommt.
//
// Phase 3 (design reboot): Elementshape ist v2.2 — name, tolMode, tol /
// tolUpper / tolLower, norm, distribution. Der globale `currentStandard`
// ist weg; Normen leben pro Element auf `norm`.

export const TEMPLATES = [
  {
    id: 'welle-bohrung-spiel',
    name: 'Spielpassung Welle/Bohrung',
    description: 'Klassische Welle-Bohrung-Paarung mit ISO-Passung H7/g6 - zeigt das resultierende Spiel.',
    icon: '⚙️',
    tags: ['Maschinenbau', 'Anfänger'],
    mode: 'worstcase',
    elements: [
      // Bohrung H7 bei Ø30: lower 0, upper +0.021 → asymmetric
      { id: 'tpl-1', name: 'Bohrung 30 H7', nominal: 30, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0.021, tolLower: 0,
        distribution: 'normal', norm: 'bores_H7' },
      // Welle g6 bei Ø30: lower -0.020, upper -0.007 → asymmetric
      { id: 'tpl-2', name: 'Welle 30 g6', nominal: 30, direction: -1,
        tolMode: 'asymmetric', tol: 0, tolUpper: -0.007, tolLower: -0.020,
        distribution: 'normal', norm: 'shafts_g6' },
    ],
    limits: { min: 0.007, max: 0.041 } // Spiel-Bereich aus den Passungstabellen
  },
  {
    id: 'plattenstapel-3',
    name: 'Plattenstapel (3 Lagen)',
    description: 'Drei aufeinanderliegende Platten, jeweils ISO 2768-m. Demonstriert die einfache Toleranzsumme.',
    icon: '📚',
    tags: ['Allgemein', 'Anfänger'],
    mode: 'rss',
    elements: [
      { id: 'tpl-1', name: 'Platte 1', nominal: 5, direction: 1,
        tolMode: 'symmetric', tol: 0.1, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      { id: 'tpl-2', name: 'Platte 2', nominal: 8, direction: 1,
        tolMode: 'symmetric', tol: 0.1, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      { id: 'tpl-3', name: 'Platte 3', nominal: 12, direction: 1,
        tolMode: 'symmetric', tol: 0.15, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
    ],
    limits: { min: 24.7, max: 25.3 }
  },
  {
    id: 'gehaeuse-deckel-dichtung',
    name: 'Gehäuse + Deckel + Dichtung',
    description: 'Gehäusebohrung, Deckelvertiefung und Dichtung - Berechnung der verbleibenden Pressung der Dichtung.',
    icon: '🔧',
    tags: ['Montage', 'Fortgeschritten'],
    mode: 'worstcase',
    elements: [
      { id: 'tpl-1', name: 'Gehäusetiefe', nominal: 20, direction: 1,
        tolMode: 'symmetric', tol: 0.2, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      { id: 'tpl-2', name: 'Deckelvertiefung', nominal: 18, direction: -1,
        tolMode: 'symmetric', tol: 0.2, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      // Dichtung is manual (no specific norm) — empty `norm` keeps the
      // stored tol values as the source of truth.
      { id: 'tpl-3', name: 'Dichtung (uncompressed)', nominal: 2.5, direction: -1,
        tolMode: 'symmetric', tol: 0.1, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    // Negative Werte = Dichtungspressung (ungeprüfte Spielraum-Mindestpressung)
    limits: { min: null, max: null }
  },
  {
    id: 'spritzguss-tg3-kette',
    name: 'Spritzguss-Kette DIN 16742 TG 3',
    description: 'Drei werkzeuggebundene Maße aus einem Spritzgussteil, Toleranzgruppe TG 3 (W).',
    icon: '🧊',
    tags: ['Kunststoff', 'Fertigung'],
    mode: 'worstcase',
    elements: [
      // DIN 16742 → suggestDistribution returns 'triangular'; we set
      // it explicitly here so the template doesn't surprise anyone
      // reading the fixture.
      { id: 'tpl-1', name: 'Steg 8 mm', nominal: 8, direction: 1,
        tolMode: 'symmetric', tol: 0.05, tolUpper: 0, tolLower: 0,
        distribution: 'triangular', norm: 'tg3_wb' },
      { id: 'tpl-2', name: 'Wand 12 mm', nominal: 12, direction: 1,
        tolMode: 'symmetric', tol: 0.06, tolUpper: 0, tolLower: 0,
        distribution: 'triangular', norm: 'tg3_wb' },
      { id: 'tpl-3', name: 'Aufnahme 24 mm', nominal: 24, direction: -1,
        tolMode: 'symmetric', tol: 0.07, tolUpper: 0, tolLower: 0,
        distribution: 'triangular', norm: 'tg3_wb' },
    ],
    limits: { min: -4.2, max: 4.2 }
  },

  // ── Oberflächen-Templates (Oberflächenauftrag) ──
  // Beschichtungen wirken in der Maßkette wie eigene Glieder. Angegeben
  // ist der AUFTRAG je Seite (Maßänderung), nicht die Schichtdicke: bei
  // Eloxal wächst nur ein Teil der Schicht nach außen (je nach Verfahren
  // etwa ein Drittel bis die Hälfte), Beizen trägt vorher ab. Den Auftrag
  // liefert der Beschichter. Galvanische Schichten (Hartchrom) wachsen
  // vollständig nach außen, dort sind Schicht und Auftrag gleich.
  //   * Auftrag auf der Welle (außen) → Durchmesser nimmt zu (dir=+1).
  //   * Auftrag in der Bohrung (innen) → effektiver Durchmesser nimmt ab (dir=-1).
  //   * Auftrag auf einer Fläche → wirkt einseitig auf die Tiefe.

  {
    id: 'welle-eloxal-h7',
    name: 'Welle ⌀20 h7 + Auftrag 10 µm',
    description: 'Aluminium-Welle ⌀20 (h7) mit 10 µm Oberflächenauftrag je Seite, z. B. Eloxal. Angegeben ist der Auftrag, nicht die Schichtdicke. Der Durchmesser nimmt um 20 µm zu.',
    icon: '🔩',
    tags: ['Oberflächen', 'Maschinenbau'],
    mode: 'worstcase',
    elements: [
      // shafts h7 bei ⌀18-30: u=0, l=-0.021
      { id: 'tpl-1', name: 'Welle ⌀20 h7', nominal: 20, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0, tolLower: -0.021,
        distribution: 'normal', norm: 'shafts_h7' },
      // Auftrag 2× 10 µm = 0.020 mm Durchmesserzuwachs (beide Seiten
      // der Welle), Toleranz ±3 µm je Seite → ±0,006 am Durchmesser.
      { id: 'tpl-2', name: 'Auftrag (2× 10 µm)', nominal: 0.020, direction: 1,
        tolMode: 'symmetric', tol: 0.006, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    limits: { min: null, max: null }
  },

  {
    id: 'bohrung-eloxal-h7',
    name: 'Bohrung ⌀10 H7 + Auftrag 5 µm',
    description: 'Bohrung ⌀10 H7 in Aluminium mit 5 µm Oberflächenauftrag je Seite, z. B. Eloxal. Angegeben ist der Auftrag, nicht die Schichtdicke. Der wirksame Durchmesser nimmt um 10 µm ab.',
    icon: '⭕',
    tags: ['Oberflächen', 'Passung'],
    mode: 'worstcase',
    elements: [
      // bores H7 bei ⌀6-10: u=0.015, l=0
      { id: 'tpl-1', name: 'Bohrung ⌀10 H7', nominal: 10, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0.015, tolLower: 0,
        distribution: 'normal', norm: 'bores_H7' },
      // Auftrag innen: 2× 5 µm = 0.010 mm Durchmesserreduktion,
      // Toleranz ±2 µm je Seite. Richtung -1, weil vom Innenmaß abgezogen.
      { id: 'tpl-2', name: 'Auftrag (2× 5 µm)', nominal: 0.010, direction: -1,
        tolMode: 'symmetric', tol: 0.004, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    limits: { min: null, max: null }
  },

  {
    id: 'passung-h7-g6-eloxal-beidseitig',
    name: 'Passung H7/g6 ⌀30 mit Oberflächenauftrag',
    description: 'Spielpassung ⌀30 H7/g6, Bohrung und Welle beschichtet mit 15 µm Auftrag je Seite (z. B. Harteloxal). Angegeben ist der Auftrag, nicht die Schichtdicke. Zeigt, wie die Beschichtung das Spiel auffrisst: aus Spiel wird Übermaß.',
    icon: '🛠',
    tags: ['Oberflächen', 'Passung', 'Fortgeschritten'],
    mode: 'worstcase',
    elements: [
      // Bohrung ⌀30 H7: u=0.021, l=0
      { id: 'tpl-1', name: 'Bohrung ⌀30 H7', nominal: 30, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0.021, tolLower: 0,
        distribution: 'normal', norm: 'bores_H7' },
      // Auftrag innen (Bohrung): reduziert die effektive Bohrung
      { id: 'tpl-2', name: 'Auftrag Bohrung (2× 15 µm)', nominal: 0.030, direction: -1,
        tolMode: 'symmetric', tol: 0.010, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
      // Welle ⌀30 g6: u=-0.007, l=-0.020. Richtung -1, weil wir Spiel
      // = Bohrung − Welle rechnen.
      { id: 'tpl-3', name: 'Welle ⌀30 g6', nominal: 30, direction: -1,
        tolMode: 'asymmetric', tol: 0, tolUpper: -0.007, tolLower: -0.020,
        distribution: 'normal', norm: 'shafts_g6' },
      // Auftrag außen (Welle): vergrößert die effektive Welle, reduziert
      // damit das Spiel — daher hier direction=-1, weil das Glied vom
      // Spiel abgezogen wird (genauso wie die Welle selbst).
      { id: 'tpl-4', name: 'Auftrag Welle (2× 15 µm)', nominal: 0.030, direction: -1,
        tolMode: 'symmetric', tol: 0.010, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    limits: { min: null, max: null }
  },

  {
    id: 'alu-gehaeuse-hartanodisation',
    name: 'Alu-Gehäuse + Hartanodisation, Auftrag 25 µm',
    description: 'Tasche im Aluminium-Gehäuse, Tiefe 40 mm, Bodenfläche hartanodisiert mit 25 µm Auftrag (±5 µm). Angegeben ist der Auftrag, nicht die Schichtdicke. Der Auftrag reduziert die wirksame Tiefe.',
    icon: '🛡',
    tags: ['Oberflächen', 'Gehäuse', 'Fortgeschritten'],
    mode: 'worstcase',
    elements: [
      // Gehäusetiefe als allgemeintoleriertes Maß (ISO 2768-m bei 40 mm:
      // ±0,3 mm) — Engine liest die Werte aus der Norm.
      { id: 'tpl-1', name: 'Gehäusetasche tief', nominal: 40, direction: 1,
        tolMode: 'symmetric', tol: 0.3, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      // Hartanodisation einseitig auf dem Boden: 25 µm Auftrag, ±5 µm.
      { id: 'tpl-2', name: 'Auftrag Boden, Hartanodisation (25 µm)', nominal: 0.025, direction: -1,
        tolMode: 'symmetric', tol: 0.005, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    limits: { min: null, max: null }
  },

  {
    id: 'welle-hartchrom-lauflaeche',
    name: 'Welle ⌀30 h7 + Hartchrom-Lauffläche 10 µm',
    description: 'Welle ⌀30 (h7) mit galvanischer Hartverchromung als Lauffläche (10 µm pro Seite, ±5 µm). Hartchrom wächst vollständig nach außen, Schicht und Auftrag sind gleich.',
    icon: '⚡',
    tags: ['Oberflächen', 'Antrieb'],
    mode: 'worstcase',
    elements: [
      // shafts h7 bei ⌀18-30: u=0, l=-0.021
      { id: 'tpl-1', name: 'Welle ⌀30 h7', nominal: 30, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0, tolLower: -0.021,
        distribution: 'normal', norm: 'shafts_h7' },
      // Hartchrom-Schicht 2× 10 µm = 0.020 mm Durchmesserzuwachs;
      // Schichtdicken-Toleranz typ. ±5 µm beidseitig (DIN 50961).
      { id: 'tpl-2', name: 'Auftrag Hartchrom (2× 10 µm)', nominal: 0.020, direction: 1,
        tolMode: 'symmetric', tol: 0.005, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    limits: { min: null, max: null }
  },
  // Beispiel aus dem Wissensartikel „Toleranzen berechnen“: Axialspiel
  // eines Zahnrads zwischen Wellenbund und Sicherungsring DIN 471 (Ø30,
  // Ringdicke 1,5). Die Rechtwinkligkeit der Nabenstirnfläche ist ein
  // eigenes Glied mit Nennmaß 0 (ISO 8015: Form/Lage zusätzlich zum Maß).
  {
    id: 'zahnrad-sicherungsring-axialspiel',
    name: 'Zahnrad mit Sicherungsring (Axialspiel)',
    description: 'Zahnrad auf einer Welle, links am Wellenbund, rechts mit Sicherungsring DIN 471 gesichert. Das Axialspiel muss ≥ 0 bleiben, sonst lässt sich der Ring nicht montieren. Mit Rechtwinkligkeit der Nabe als eigenem Glied.',
    icon: '⚙',
    tags: ['Maschinenbau', 'Maßkette'],
    mode: 'worstcase',
    elements: [
      // ISO 2768-m bei 20 mm: ±0,2
      { id: 'tpl-1', name: 'Wellenbund bis tragende Nutflanke', nominal: 20, direction: 1,
        tolMode: 'symmetric', tol: 0.2, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      { id: 'tpl-2', name: 'Nabenbreite Zahnrad', nominal: 18, direction: -1,
        tolMode: 'symmetric', tol: 0.2, tolUpper: 0, tolLower: 0,
        distribution: 'normal', norm: 'iso2768m' },
      { id: 'tpl-3', name: 'Sicherungsring DIN 471, Dicke', nominal: 1.5, direction: -1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0, tolLower: -0.06,
        distribution: 'normal', norm: '' },
      { id: 'tpl-4', name: 'Rechtwinkligkeit Nabenstirnfläche', nominal: 0, direction: -1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0.1, tolLower: 0,
        distribution: 'normal', norm: '' },
    ],
    limits: { min: 0, max: null }
  },
  // ─── Wärmeausdehnungs-Demos (Task #27 Phase G) ─────────────────────
  // Beide Templates tragen `thermal` mit enabled=true, sodass das
  // Feature beim Laden sofort sichtbar ist. Free-User sehen die
  // Material-Spalte und die thermisch verschobenen Resultate, können
  // aber Material + Thermal-Range nicht ändern (Pro-Gate in der UI).
  {
    id: 'lagersitz-stahl-alu',
    name: 'Lagersitz Stahlwelle in Alu-Gehäuse',
    description: 'Klassischer Materialmix: Stahlwelle ⌀30 g6 in Alu-Bohrung ⌀30 H7. Bei 80 °C läuft das Spiel deutlich auseinander - bei −20 °C wird es knapp.',
    icon: '🌡️',
    tags: ['Wärmeausdehnung', 'Maschinenbau', 'Materialmix'],
    mode: 'worstcase',
    elements: [
      // Bohrung ⌀30 H7 in Aluminium: u=+0.021, l=0
      { id: 'tpl-1', name: 'Alu-Bohrung ⌀30 H7', nominal: 30, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0.021, tolLower: 0,
        distribution: 'normal', norm: 'bores_H7', material: 'alu_6060' },
      // Welle ⌀30 g6 in Stahl: u=-0.007, l=-0.020
      { id: 'tpl-2', name: 'Stahl-Welle ⌀30 g6', nominal: 30, direction: -1,
        tolMode: 'asymmetric', tol: 0, tolUpper: -0.007, tolLower: -0.020,
        distribution: 'normal', norm: 'shafts_g6', material: 'steel_s235jr' },
    ],
    thermal: { enabled: true, tMin: -20, tMax: 80 },
    limits: { min: null, max: null }
  },
  {
    id: 'pom-buchse-stahlbohrung',
    name: 'POM-Gleitbuchse in Stahlbohrung',
    description: 'Kunststoffbuchse aus POM-C in einer Stahlbohrung, Betrieb bis 60 °C. Zeigt den dominanten thermischen Effekt bei Kunststoffen - Spielwert ändert sich um ein Vielfaches der Fertigungstoleranz.',
    icon: '🌡️',
    tags: ['Wärmeausdehnung', 'Kunststoff', 'Anfänger'],
    mode: 'worstcase',
    elements: [
      // Bohrung ⌀20 H7 in Stahl: u=+0.021, l=0
      { id: 'tpl-1', name: 'Stahl-Bohrung ⌀20 H7', nominal: 20, direction: 1,
        tolMode: 'asymmetric', tol: 0, tolUpper: 0.021, tolLower: 0,
        distribution: 'normal', norm: 'bores_H7', material: 'steel_s235jr' },
      // POM-Buchse ⌀20 f7: u=-0.020, l=-0.041
      { id: 'tpl-2', name: 'POM-Buchse ⌀20 f7', nominal: 20, direction: -1,
        tolMode: 'asymmetric', tol: 0, tolUpper: -0.020, tolLower: -0.041,
        distribution: 'normal', norm: 'shafts_f7', material: 'plastic_pom_c' },
    ],
    thermal: { enabled: true, tMin: 0, tMax: 60 },
    limits: { min: null, max: null }
  }
];

/**
 * Builds the canonical state shape that toleranceStore.loadProject expects.
 * Templates have no derived M_0 yet; the store will recompute via calculate().
 */
export function templateToProjectData(template) {
  return {
    elements: template.elements.map(el => ({ ...el })), // shallow clone to avoid shared refs
    mode: template.mode,
    m0: {
      nominal: 0,
      min: 0,
      max: 0,
      tolerance: 0,
      status: 'none',
      limits: template.limits ?? { min: null, max: null }
    },
    // Optional thermal-Block, nur in Templates ab Task #27. Wird vom
    // toleranceStore.loadProject via cloneThermal verarbeitet und fällt
    // auf disabled-Defaults zurück, wenn das Template (wie alle bisherigen)
    // gar keine thermal-Section trägt.
    ...(template.thermal ? { thermal: template.thermal } : {})
  };
}
